import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';

// API Response Interface
export interface CourseApiResponse {
  id: string;
  title: string;
  description: string;
  instructor: string;
  duration: string;
  category: string;
  level: string;
  thumbnail: string | null;
  price: string;
  isActive: boolean;
  courseStatusId?: number;
  seat?: number;
  regisSeat?: number;
  trainingLocation?: string;
  startDate: string | null;
  endDate: string | null;
  closeRegisterDate?: string | null;
  certificationBySEC?: string | boolean;
  imageThumbnail?: string | null;
  imageDetail?: string | null;
  createdAt: string;
  updatedAt: string;
  userCourses: Array<{
    id: string;
    enrolledAt: string;
    completedAt: string | null;
    progress: string;
    isCompleted: boolean;
    totalHoursStudied: string;
    notes: string;
    createdAt: string;
    updatedAt: string;
  }>;
  cpdHours: Array<{
    id: string;
    hoursStudied: string;
    studyDate: string;
    year: number;
    sessionTitle: string;
    notes: string;
    isVerified: boolean;
    createdAt: string;
    updatedAt: string;
  }>;
}

export interface CourseDetail {
  id?: string;
  courseTopic: string;
  category: string;
  cpdHours: number;
  trainingFormat: string;
  startDate: string | null;
  endDate: string | null;
  closeRegisterDate?: string | null;
  trainingLocation?: string;
  courseFee: number;
  maxParticipants: number;
  courseStatus: string;
  courseStatusId?: number;
  seat?: number;
  regisSeat?: number;
  remarks?: string;
  posterImageUrl?: string;
  imageDetailUrl?: string;
  instructorName?: string;
  certificationBySEC?: string | boolean;
  rating?: number;
  reviewCount?: number;
  lessonCount?: number;
  enrolledCount?: number;
  duration?: string;
  level?: string;
  whatYouWillLearn?: string[];
  whoShouldAttend?: string[];
  courseDescription?: string;
  courseObjectives?: string[];
}

@Component({
  selector: 'app-user-course-register',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './user-course-register.component.html',
  styleUrl: './user-course-register.component.scss'
})
export class UserCourseRegisterComponent implements OnInit {
  courseId: string | null = null;
  courseData: CourseDetail | null = null;
  isLoading = false;
  activeTab: string = 'overview';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('🚀 UserCourseRegister: Component initialized');
    
    // Subscribe to route params to handle navigation changes
    this.route.paramMap.subscribe(params => {
      this.courseId = params.get('id');
      console.log('📋 Course ID from route:', this.courseId);
      
      if (this.courseId) {
        this.loadCourseData(this.courseId);
      } else {
        console.error('❌ No course ID provided');
        alert('ไม่พบรหัสคอร์ส');
        this.goBack();
      }
    });
  }

  /**
   * Load course data from API
   */
  private loadCourseData(courseId: string): void {
    this.isLoading = true;
    const apiUrl = `${environment.apiUrl}/courses/${courseId}`;
    console.log('📡 Fetching course data from:', apiUrl);
    
    this.http.get<CourseApiResponse>(apiUrl, {
      observe: 'response' // Get full response to handle 304
    }).subscribe({
      next: (response) => {
        console.log('✅ API Response Status:', response.status);
        console.log('✅ API Response Data:', response.body);
        
        // Handle both 200 and 304 status codes
        if (response.body) {
          this.courseData = this.mapApiResponseToCourseDetail(response.body);
          console.log('✅ Course data mapped:', this.courseData);
        } else {
          console.warn('⚠️ Response body is null (304 Not Modified)');
        }
        
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading course data:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        
        this.isLoading = false;
        
        if (error.status === 404) {
          alert('ไม่พบคอร์สที่ต้องการ');
        } else if (error.status === 0) {
          alert('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่า Backend ทำงานอยู่');
        } else {
          alert('ไม่สามารถโหลดข้อมูลคอร์สได้ กรุณาลองใหม่อีกครั้ง');
        }
        
        this.goBack();
      }
    });
  }

  /**
   * Map API response to CourseDetail interface
   */
  private mapApiResponseToCourseDetail(apiData: CourseApiResponse): CourseDetail {
    console.log('🔄 Mapping API data to CourseDetail...');
    console.log('📊 API Data:', apiData);
    
    const enrolledCount = apiData.userCourses?.length || 0;

    const levelToFormat: { [key: string]: string } = {
      'beginner': 'onsite',
      'intermediate': 'online',
      'advanced': 'hybrid'
    };

    const categoryToStatus: { [key: string]: string } = {
      'Accounting': 'open',
      'Property Valuation': 'ongoing',
      'Machinery Valuation': 'open',
      'Business Valuation': 'open',
      'Management Accounting': 'open',
      'Ethics': 'open',
      'Standards': 'open',
      'Technology': 'open',
      'Financial Reporting': 'open'
    };

    return {
      id: apiData.id,
      courseTopic: apiData.title,
      category: apiData.category,
      cpdHours: parseFloat(apiData.duration) || 0,
      trainingFormat: levelToFormat[apiData.level] || 'online',
      startDate: apiData.startDate,
      endDate: apiData.endDate,
      closeRegisterDate: apiData.closeRegisterDate || null,
      trainingLocation: apiData.trainingLocation || this.getTrainingLocationFromApi(apiData),
      courseFee: parseFloat(apiData.price),
      maxParticipants: 100,
      courseStatus: categoryToStatus[apiData.category] || 'open',
      courseStatusId: apiData.courseStatusId,
      seat: apiData.seat || 0,
      regisSeat: apiData.regisSeat || 0,
      remarks: apiData.description,
      posterImageUrl: apiData.thumbnail || undefined,
      imageDetailUrl: apiData.imageDetail || undefined,
      instructorName: apiData.instructor || 'ผู้สอน VAT',
      certificationBySEC: apiData.certificationBySEC,
      rating: 4.5 + Math.random() * 0.5,
      reviewCount: Math.floor(Math.random() * 100) + 50,
      lessonCount: apiData.cpdHours?.length || 0,
      enrolledCount: enrolledCount,
      duration: `${apiData.duration || 0} ชั่วโมง`,
      level: apiData.level || 'beginner',
      whatYouWillLearn: [
        `เรียนรู้หลักการและแนวคิดพื้นฐานของ${apiData.category}`,
        `พัฒนาทักษะการวิเคราะห์และประเมินผลในระดับ ${this.getLevelDisplayText(apiData.level)}`,
        `ฝึกปฏิบัติจริงกับกรณีศึกษาที่หลากหลาย`,
        `เข้าใจมาตรฐานและข้อกำหนดทางวิชาชีพ`
      ],
      courseDescription: apiData.description,
      courseObjectives: [
        `เข้าใจหลักการพื้นฐานของ ${apiData.category}`,
        `สามารถประยุกต์ใช้ความรู้ในการทำงานจริง`,
        `พัฒนาทักษะการคิดวิเคราะห์และแก้ปัญหา`,
        `สร้างเครือข่ายและแลกเปลี่ยนประสบการณ์กับผู้เชี่ยวชาญ`
      ],
      whoShouldAttend: [
        `ผู้ที่สนใจศึกษาด้าน ${apiData.category}`,
        `ผู้ประกอบวิชาชีพที่ต้องการพัฒนาทักษะ`,
        `นักศึกษาและบัณฑิตจบใหม่`,
        `ผู้บริหารและเจ้าของธุรกิจ`
      ]
    };
  }

  /**
   * Get training location from API data
   */
  private getTrainingLocationFromApi(apiData: CourseApiResponse): string {
    // Check if API has a specific location field (adjust field name as needed)
    // For now, derive from training format
    const format = apiData.level;
    
    if (format === 'beginner') {
      return 'ห้องประชุม VAT ชั้น 5';
    } else if (format === 'intermediate') {
      return 'ออนไลน์ผ่าน Zoom';
    } else if (format === 'advanced') {
      return 'ห้องประชุม VAT ชั้น 5 และออนไลน์ผ่าน Zoom';
    }
    
    return 'ออนไลน์ผ่าน Zoom';
  }

  /**
   * Get level display text in Thai
   */
  getLevelDisplayText(level: string): string {
    const levelMap: { [key: string]: string } = {
      'beginner': 'พื้นฐาน',
      'intermediate': 'ปานกลาง',
      'advanced': 'ขั้นสูง'
    };
    return levelMap[level] || level;
  }

  /**
   * Get SEC certification display text
   */
  getCertificationDisplayText(certification: string | boolean | undefined): string {
    if (!certification && certification !== false) return 'ไม่รับรอง';
    
    // Handle boolean values (true/false as strings or actual booleans)
    if (certification === 'true' || certification === true) {
      return 'รับรอง';
    } else if (certification === 'false' || certification === false) {
      return 'ไม่รับรอง';
    }
    
    // If it's already a Thai text, return as is
    return certification;
  }

  /**
   * Get level badge class
   */
  getLevelBadgeClass(level: string): string {
    const levelMap: { [key: string]: string } = {
      'beginner': 'badge-success',
      'intermediate': 'badge-warning',
      'advanced': 'badge-danger'
    };
    return levelMap[level] || 'badge-secondary';
  }

  /**
   * Change active tab
   */
  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  /**
   * Check if tab is active
   */
  isActiveTab(tab: string): boolean {
    return this.activeTab === tab;
  }

  /**
   * Get training format label
   */
  getTrainingFormatLabel(format: string): string {
    const formats: { [key: string]: string } = {
      'onsite': 'อบรมในสถานที่',
      'online': 'อบรมออนไลน์',
      'hybrid': 'อบรมแบบผสม'
    };
    return formats[format] || format;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        // Course dates are naive wall-clock tagged UTC; format in UTC to avoid
        // the +7h (ICT) shift that rolls evening times to the next calendar day.
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Format price with currency
   */
  formatPrice(price: number): string {
    return new Intl.NumberFormat('th-TH', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  }

  /**
   * Generate star rating array
   */
  getStarRating(rating: number): { full: number; half: boolean; empty: number } {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    return {
      full: fullStars,
      half: hasHalfStar,
      empty: emptyStars
    };
  }

  /**
   * Get course poster URL with fallback
   */
  getCoursePosterUrl(): string {
    if (this.courseData?.posterImageUrl) {
      return this.courseData.posterImageUrl;
    }
    
    // Return SVG data URL for placeholder (without Thai text to avoid btoa error)
    // Use encodeURIComponent instead of btoa for Thai characters
    const svgContent = `
      <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#667eea;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#764ba2;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="400" height="300" fill="url(#grad)" />
      </svg>
    `;
    
    // Use data URI with charset=utf-8 to support Thai characters
    return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgContent);
  }

  /**
   * Enroll in course
   */
  enrollCourse(): void {
    if (!this.courseData?.id) {
      alert('ไม่พบข้อมูลคอร์ส');
      return;
    }
    
    console.log('🎓 Starting course enrollment...');
    console.log('📋 Course ID:', this.courseData.id);
    
    // Get userId from localStorage (check both 'currentUser' and 'userData')
    let userDataStr = localStorage.getItem('currentUser');
    if (!userDataStr) {
      userDataStr = localStorage.getItem('userData');
    }
    
    console.log('🔍 Checking localStorage for user data...');
    console.log('📦 currentUser:', localStorage.getItem('currentUser'));
    console.log('📦 userData:', localStorage.getItem('userData'));
    
    if (!userDataStr) {
      console.error('❌ No user data found in localStorage');
      alert('กรุณาเข้าสู่ระบบก่อนลงทะเบียน');
      this.router.navigate(['/login']);
      return;
    }
    
    let userId: string;
    try {
      const userData = JSON.parse(userDataStr);
      console.log('👤 Parsed user data:', userData);
      
      // Try multiple possible field names for userId
      userId = userData.id || userData.userId || userData.user?.id;
      
      if (!userId) {
        console.error('❌ No userId found in userData:', userData);
        console.error('❌ Available keys:', Object.keys(userData));
        alert('ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่');
        this.router.navigate(['/login']);
        return;
      }
      
      console.log('✅ User ID found:', userId);
    } catch (error) {
      console.error('❌ Error parsing userData:', error);
      alert('ข้อมูลผู้ใช้ไม่ถูกต้อง กรุณาเข้าสู่ระบบใหม่');
      this.router.navigate(['/login']);
      return;
    }
    
    // Prepare enrollment data
    const enrollmentData = {
      userId: userId,
      courseId: this.courseData.id,
      hoursStudied: this.courseData.cpdHours,
      studyDate: this.courseData.endDate || new Date().toISOString(),
      year: new Date().getFullYear() + 543, // Thai Buddhist year
      sessionTitle: this.courseData.courseTopic,
      notes: `ลงทะเบียนเรียนคอร์ส: ${this.courseData.courseTopic}`,
      isVerified: false
    };
    
    console.log('📤 Enrollment data:', enrollmentData);
    
    // Confirm enrollment
    const confirmMessage = `ยืนยันการลงทะเบียนคอร์ส\n\n` +
      `คอร์ส: ${this.courseData.courseTopic}\n` +
      `CPD: ${this.courseData.cpdHours} ชั่วโมง\n` +
      `ค่าลงทะเบียน: ฿${this.formatPrice(this.courseData.courseFee)}\n\n` +
      `ต้องการดำเนินการต่อหรือไม่?`;
    
    if (!confirm(confirmMessage)) {
      console.log('❌ User cancelled enrollment');
      return;
    }
    
    // Send enrollment request
    this.isLoading = true;
    const apiUrl = `${environment.apiUrl}/cpd-hours`;
    console.log('📡 Sending enrollment to:', apiUrl);
    
    this.http.post(apiUrl, enrollmentData).subscribe({
      next: (response) => {
        console.log('✅ Enrollment successful:', response);
        this.isLoading = false;
        
        alert('ลงทะเบียนสำเร็จ!\n\nคุณสามารถเข้าเรียนได้ทันที');
        
        // Navigate back to dashboard
        this.router.navigate(['/user/dashboard']);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Enrollment error:', error);
        console.error('❌ Error status:', error.status);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error body:', error.error);
        
        this.isLoading = false;
        
        let errorMessage = 'ไม่สามารถลงทะเบียนได้ กรุณาลองใหม่อีกครั้ง';
        
        if (error.status === 400) {
          errorMessage = 'ข้อมูลไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่';
        } else if (error.status === 409) {
          errorMessage = 'คุณได้ลงทะเบียนคอร์สนี้แล้ว';
        } else if (error.status === 404) {
          errorMessage = 'ไม่พบข้อมูลคอร์สหรือผู้ใช้';
        } else if (error.status === 0) {
          errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบว่า Backend ทำงานอยู่';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        
        alert(errorMessage);
      }
    });
  }

  /**
   * Check if seats are full
   */
  isSeatsFullyBooked(): boolean {
    if (!this.courseData?.seat || !this.courseData?.regisSeat) {
      return false;
    }
    return this.courseData.regisSeat >= this.courseData.seat;
  }

  /**
   * Get seat availability text
   */
  getSeatAvailabilityText(): string {
    const regisSeat = this.courseData?.regisSeat || 0;
    const seat = this.courseData?.seat || 0;
    return `${regisSeat} คน / ${seat} คน`;
  }

  /**
   * Go back to dashboard
   */
  goBack(): void {
    this.router.navigate(['/user/dashboard']);
  }
}
